import { useMutation } from "@apollo/client/react"
import Button from "@mui/material/Button"
import Link from "@mui/material/Link"
import { Formik } from "formik"
import React, { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import AuthLayout from "@/components/auth/auth_layout"
import { resetPasswordSchema } from "@/components/auth/validation_schemas"
import { TextField } from "@/components/form"
import { PublicResetPasswordErrorEnum, ResetPasswordDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

const STYLES = {
  heading: "mb-4 mt-0 font-semibold text-4xl",
  description: "text-sm text-gray-600 mb-6",
  form: "w-full",
  fieldsContainer: "flex flex-col gap-4",
  errorText: "mt-2 text-sm text-red-600",
  submitButton: "mt-2",
  successContainer: "flex flex-col gap-4 items-center text-center w-full",
  successTitle: "text-2xl font-semibold",
  successDescription: "text-base text-gray-600",
  invalidContainer: "flex flex-col gap-4 items-center text-center w-full",
  invalidTitle: "text-xl font-semibold",
  invalidDescription: "text-sm text-gray-600",
  contentContainer: "flex flex-col gap-4 min-w-100 w-full",
  linkContainer: "mt-4 text-center",
} as const

type FormValues = {
  password: string
  passwordConfirmation: string
}

const renderSuccessView = (onNavigate: (path: string) => void) => (
  <div className={STYLES.successContainer} data-testid="auth-reset-password-success">
    <div className={STYLES.successTitle}>{i18n.t("app.auth.reset_password.success_title")}</div>
    <div className={STYLES.successDescription}>{i18n.t("app.auth.reset_password.success_description")}</div>
    <Link
      href="/sign_in"
      variant="body1"
      onClick={(e) => {
        e.preventDefault()
        onNavigate("/sign_in")
      }}
      data-testid="auth-reset-password-sign-in-link"
    >
      {i18n.t("app.auth.reset_password.back_to_sign_in")}
    </Link>
  </div>
)

const renderInvalidTokenView = (onNavigate: (path: string) => void) => (
  <div className={STYLES.invalidContainer} data-testid="auth-reset-password-invalid-token">
    <div className={STYLES.invalidTitle}>{i18n.t("app.auth.reset_password.invalid_token_title")}</div>
    <div className={STYLES.invalidDescription}>{i18n.t("app.auth.reset_password.invalid_token_description")}</div>
    <Link
      href="/sign_in/forgot_password"
      variant="body1"
      onClick={(e) => {
        e.preventDefault()
        onNavigate("/sign_in/forgot_password")
      }}
      data-testid="auth-reset-password-request-new-link"
    >
      {i18n.t("app.auth.reset_password.request_new_link")}
    </Link>
  </div>
)

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [resetPassword] = useMutation(ResetPasswordDocument)
  const [success, setSuccess] = useState(false)
  const [invalidToken, setInvalidToken] = useState(false)
  const [error, setError] = useState("")

  const initialValues: FormValues = { password: "", passwordConfirmation: "" }

  const onSubmit = async (
    values: FormValues,
    {
      setSubmitting,
      setFieldError,
    }: { setSubmitting: (v: boolean) => void; setFieldError: (f: string, m: string) => void },
  ) => {
    setError("")

    if (!token) {
      setInvalidToken(true)
      setSubmitting(false)
      return
    }

    try {
      const { data } = await resetPassword({
        variables: {
          token,
          password: values.password,
          passwordConfirmation: values.passwordConfirmation,
        },
      })

      const result = data?.publicResetPassword

      if (result?.success) {
        setSuccess(true)
      } else {
        const firstError = result?.errors?.[0]
        if (firstError?.code === PublicResetPasswordErrorEnum.InvalidToken) {
          setInvalidToken(true)
        } else if (firstError?.code === PublicResetPasswordErrorEnum.ValidationError) {
          setFieldError("password", firstError.message || i18n.t("app.auth.reset_password.generic_error"))
        } else {
          setError(i18n.t("app.auth.reset_password.generic_error"))
        }
      }
    } catch (err) {
      showErrorToast()
      console.error(err)
      setError(i18n.t("app.auth.reset_password.generic_error"))
    } finally {
      setSubmitting(false)
    }
  }

  const topNav = null

  return (
    <AuthLayout topNav={topNav} testIdPrefix="auth-reset-password">
      {success ? (
        renderSuccessView(navigate)
      ) : invalidToken ? (
        renderInvalidTokenView(navigate)
      ) : (
        <>
          <h4 className={STYLES.heading}>{i18n.t("app.auth.reset_password.header")}</h4>
          <p className={STYLES.description}>{i18n.t("app.auth.reset_password.description")}</p>

          <div className={STYLES.contentContainer}>
            <Formik
              initialValues={initialValues}
              validationSchema={resetPasswordSchema}
              onSubmit={onSubmit}
              validateAfterSubmit={true}
            >
              {({ isSubmitting, handleSubmit }) => (
                <form onSubmit={handleSubmit} className={STYLES.form} data-testid="auth-reset-password-form">
                  <div className={STYLES.fieldsContainer}>
                    <TextField
                      name="password"
                      label={i18n.t("app.auth.reset_password.password")}
                      type="password"
                      data-testid="auth-reset-password-password-input"
                    />
                    <TextField
                      name="passwordConfirmation"
                      label={i18n.t("app.auth.reset_password.password_confirmation")}
                      type="password"
                      data-testid="auth-reset-password-confirmation-input"
                    />

                    {!!error && (
                      <div className={STYLES.errorText} data-testid="auth-reset-password-error">
                        {error}
                      </div>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      fullWidth
                      disabled={isSubmitting}
                      className={STYLES.submitButton}
                      data-testid="auth-reset-password-submit-btn"
                    >
                      {isSubmitting ? "..." : i18n.t("app.auth.reset_password.submit")}
                    </Button>
                  </div>

                  <div className={STYLES.linkContainer}>
                    <Link
                      href="/sign_in"
                      variant="body2"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate("/sign_in")
                      }}
                      data-testid="auth-reset-password-sign-in-link"
                    >
                      {i18n.t("app.auth.reset_password.back_to_sign_in")}
                    </Link>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </>
      )}
    </AuthLayout>
  )
}

export default ResetPasswordPage
